import React, { createContext, useContext, useState, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// ToastContext — Système de notifications toast
// Usage dans n'importe quel composant :
//   const { showToast } = useToast();
//   showToast("Annonce ajoutée !", "success");
//   showToast("Erreur réseau", "error");
//   showToast("Nouveau message reçu", "info");
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

const ICONS = {
  success: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
};

const COLORS = {
  success: { bg: "#f0fdf4", border: "#86efac", text: "#166534", icon: "#16a34a" },
  error:   { bg: "#fef2f2", border: "#fca5a5", text: "#991b1b", icon: "#dc2626" },
  info:    { bg: "#eff6ff", border: "#93c5fd", text: "#1e40af", icon: "#2563eb" },
  warning: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e", icon: "#d97706" }
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 3500) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* ── Conteneur des toasts (coin bas-droit) ── */}
      <div style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        maxWidth: "360px",
        width: "calc(100vw - 48px)"
      }}>
        {toasts.map(toast => {
          const c = COLORS[toast.type] || COLORS.info;
          return (
            <div
              key={toast.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                padding: "14px 16px",
                backgroundColor: c.bg,
                border: `1px solid ${c.border}`,
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                animation: "slideInToast 0.3s ease",
                cursor: "default"
              }}
            >
              {/* Icône */}
              <span style={{ color: c.icon, flexShrink: 0, marginTop: "1px" }}>
                {ICONS[toast.type]}
              </span>

              {/* Message */}
              <span style={{
                flex: 1,
                fontSize: "14px",
                fontWeight: "500",
                color: c.text,
                lineHeight: "1.5"
              }}>
                {toast.message}
              </span>

              {/* Bouton fermer */}
              <button
                onClick={() => removeToast(toast.id)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: c.text,
                  opacity: 0.5,
                  padding: "0",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center"
                }}
                aria-label="Fermer"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);